// src/pages/StockDetail/StockDetail.jsx - WITH WATCHLIST EYE TOGGLE
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { stockService } from '../../services/stockService';
import { watchlistService } from '../../services/watchlistService';
import MarketPerformanceChart from '../../components/Chart/MarketPerformanceChart';
import TradingModal from '../../components/Trading/TradingModal';
import { useCurrency } from '../../context/CurrencyContext';
import './StockDetail.css';

const StockDetail = () => {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const { formatCurrency, currency } = useCurrency();
  const [stock, setStock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTradingModal, setShowTradingModal] = useState(false);
  const [selectedOrderType, setSelectedOrderType] = useState(null);

  // ── Watchlist eye state ──────────────────────────────────
  const [watched, setWatched]           = useState(false);
  const [watchLoading, setWatchLoading] = useState(true);
  const [watchWorking, setWatchWorking] = useState(false);

  useEffect(() => {
    fetchStockDetail();
    checkWatchStatus();
  }, [symbol]);

  const fetchStockDetail = async () => {
    try {
      setLoading(true);
      const stockDetail = await stockService.getStockBySymbol(symbol);
      setStock(stockDetail);
    } catch (err) {
      setError(err.message || 'Failed to fetch stock details');
    } finally {
      setLoading(false);
    }
  };

  const checkWatchStatus = async () => {
    try {
      setWatchLoading(true);
      const isWatched = await watchlistService.checkWatched(symbol);
      setWatched(isWatched);
    } catch (_) {
      setWatched(false);
    } finally {
      setWatchLoading(false);
    }
  };

  const handleToggleWatch = async () => {
    if (watchWorking) return; // debounce rapid taps
    setWatchWorking(true);

    // Optimistic UI update first
    const wasWatched = watched;
    setWatched(!wasWatched);

    try {
      if (wasWatched) {
        await watchlistService.removeFromWatchlist(symbol);
      } else {
        await watchlistService.addToWatchlist(symbol);
      }
      // Notify dashboard WatchlistSection to refetch
      window.dispatchEvent(new CustomEvent('watchlistChange'));
    } catch (err) {
      console.error('Watchlist toggle error:', err);
      // Revert optimistic update on failure
      setWatched(wasWatched);
    } finally {
      setWatchWorking(false);
    }
  };

  const handleTradeClick = (orderType) => {
    setSelectedOrderType(orderType);
    setShowTradingModal(true);
  };

  const getListingStageText = () => {
    if (!stock) return '';
    switch (stock.listing_stage) {
      case 'PRIVATE_EQUITY': return 'Private Equity';
      case 'PRE_IPO':        return 'SCALE-UP ASSETS';
      case 'LISTED':         return 'Listed';
      case 'FPO':            return 'GROWTH ROUNDS ASSETS';
      default:               return stock.listing_stage_display || 'Unknown';
    }
  };

  const renderInvestmentDetails = () => {
    const investmentData = [
      ['Minimum investment (N)', stock.minimum_investment,      'Business operating cycle', stock.business_operating_cycle],
      ['Funding goal',            stock.funding_goal,            'Est Profit margin',         stock.est_profit_margin],
      ['Equity offered',          stock.equity_offered,          'Est annual revenue',         stock.est_annual_revenue],
      ['Projected returns',       stock.projected_returns,       'Est Annual profit',          stock.est_annual_profit],
      ['Payout structure',        stock.payout_structure,        'Est Business value',         stock.est_business_value],
      ['Risk level',              stock.risk_level,              'Total shares',               stock.total_shares],
      ['Industry',                stock.industry,                'Use of funds',               stock.use_of_funds],
      ['Exit strategy',           stock.exit_strategy,           'Asset structure',            stock.asset_structure],
    ];

    return (
      <div className="investment-details-grid">
        {investmentData.map((row, index) => (
          <div key={index} className="investment-row">
            <div className="investment-cell label">{row[0]}</div>
            <div className="investment-cell value">{row[1] || 'Not specified'}</div>
            <div className="investment-cell label">{row[2]}</div>
            <div className="investment-cell value">{row[3] || 'Not specified'}</div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="stock-detail-container">
        <div className="loading">Loading stock details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="stock-detail-container">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="stock-detail-container">
      {/* ── Header ── */}
      <div className="detail-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          ← Back
        </button>

        <div className="stock-info-header">
          <h1>{stock.symbol} - {stock.name}</h1>
          {stock.listing_stage && (
            <span className="listing-stage-badge">{getListingStageText()}</span>
          )}
        </div>

        {/* ── Eye / Watchlist toggle (top-right) ── */}
        <button
          className={`watchlist-eye-btn ${watched ? 'watched' : 'unwatched'} ${watchWorking ? 'working' : ''}`}
          onClick={handleToggleWatch}
          disabled={watchLoading || watchWorking}
          title={watched ? 'Remove from watchlist' : 'Add to watchlist'}
          aria-label={watched ? 'Remove from watchlist' : 'Add to watchlist'}
        >
          {watchLoading || watchWorking ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="eye-spin">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeDasharray="31.4 31.4" strokeLinecap="round"/>
            </svg>
          ) : watched ? (
            /* Open eye — currently watching */
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
            </svg>
          ) : (
            /* Closed eye — not watching */
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46A11.804 11.804 0 001 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" fill="currentColor"/>
            </svg>
          )}
        </button>
      </div>

      {/* ── Chart ── */}
      <div className="chart-section">
        <MarketPerformanceChart initialStockSymbol={symbol} />
      </div>

      {/* ── About ── */}
      {stock.description && (
        <div className="stock-description-section">
          <h3>About {stock.symbol}</h3>
          <p>{stock.description}</p>
        </div>
      )}

      {/* ── Trade options ── */}
      <div className="trading-options">
        <h2>Trade Options ({currency})</h2>
        <div className="trade-buttons-grid">
          <button className="trade-button buy-market"  onClick={() => handleTradeClick('buy-market')}>Buy (Market)</button>
          <button className="trade-button sell-market" onClick={() => handleTradeClick('sell-market')}>Sell (Market)</button>
          <button className="trade-button buy-limit"   onClick={() => handleTradeClick('buy-limit')}>Buy (Limit)</button>
          <button className="trade-button sell-limit"  onClick={() => handleTradeClick('sell-limit')}>Sell (Limit)</button>
        </div>
      </div>

      {/* ── Investment details ── */}
      <div className="investment-details-section">
        {renderInvestmentDetails()}
        <p className="investment-disclaimer">
          Returns are not guaranteed, capital is at risk.
        </p>
      </div>

      {/* ── Trading modal ── */}
      {showTradingModal && stock && (
        <TradingModal
          stock={stock}
          orderType={selectedOrderType}
          onClose={() => {
            setShowTradingModal(false);
            setSelectedOrderType(null);
          }}
        />
      )}
    </div>
  );
};

export default StockDetail;